import { useEffect, useState } from "react";
import { Box, Button, HStack, Popover, PopoverArrow, PopoverBody, PopoverContent, PopoverTrigger, Text, Tooltip, Wrap, WrapItem } from "@chakra-ui/react";

const CLOCK_API = "https://clock.poc.mrc.ai/api/clocks";

/** One platform clock as a slim chip. A manual (or sim) clock can be moved on from its chip. */
const ClockChip = ({ clock }) => {
  const { _id, name, type } = clock;
  const isManual = type === "manual" || type === "sim";
  const [time, setTime] = useState(clock.currentTime);

  useEffect(() => setTime(clock.currentTime), [clock.currentTime]);
  useEffect(() => {
    if (isManual) return undefined;
    const fetchNow = async () => {
      try {
        const res = await fetch(`${CLOCK_API}/${_id}`);
        if (res.ok) setTime((await res.json()).currentTime);
      } catch {
        // keep the last time shown
      }
    };
    fetchNow();
    const timer = setInterval(fetchNow, 60000);
    return () => clearInterval(timer);
  }, [isManual, _id]);

  const advance = async (amount, unit) => {
    try {
      const res = await fetch(`${CLOCK_API}/${_id}/advance`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, unit }),
      });
      if (res.ok) setTime((await res.json()).currentTime);
    } catch {
      // the chip keeps its time
    }
  };

  const date = new Date(time);
  const valid = !Number.isNaN(date.getTime());
  const chip = (
    <HStack as={isManual ? "button" : "div"} type={isManual ? "button" : undefined} spacing={2} bg="gray.900" color="white"
      borderRadius="full" px={3} py={1} fontSize="xs" _hover={isManual ? { bg: "gray.700" } : undefined}>
      <Text textTransform="uppercase" letterSpacing="wide" color="whiteAlpha.700">{name}</Text>
      <Text fontFamily="mono" color="green.300">
        {valid ? `${date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} ${date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : "—"}
      </Text>
      {isManual && <Text color="whiteAlpha.600">▾</Text>}
    </HStack>
  );

  if (!isManual) return <Tooltip label={`${name} clock`} hasArrow>{chip}</Tooltip>;
  return (
    <Popover placement="bottom-start">
      <PopoverTrigger>{chip}</PopoverTrigger>
      <PopoverContent w="auto">
        <PopoverArrow />
        <PopoverBody>
          <Text fontSize="xs" color="gray.600" mb={1}>Move {name} on</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => advance(1, "minutes")}>+1m</Button>
            <Button size="xs" onClick={() => advance(1, "hours")}>+1h</Button>
            <Button size="xs" onClick={() => advance(1, "days")}>+1d</Button>
          </HStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

/** The platform clocks, in one slim row. */
const ClockChips = ({ clocks }) =>
  clocks.length ? (
    <Wrap spacing={2}>
      {clocks.map((c) => <WrapItem key={c._id}><ClockChip clock={c} /></WrapItem>)}
    </Wrap>
  ) : (
    <Box />
  );

export default ClockChips;
